import React, {ChangeEvent, useEffect, useState} from "react";

import {Button} from "primereact/button";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import DatasetUnitSelector from "components/basic/dropdowns/dataset-unit-selector/DatasetUnitSelector";
import {Dropdown, DropdownChangeParams} from "primereact/dropdown";
import {InputText} from "primereact/inputtext";
import {InputNumber, InputNumberValueChangeParams} from "primereact/inputnumber";
import {easasAlert} from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import {useUpdateDatasetInfo} from "api/api-hooks/datasets-hooks";
import {useGetDatasetMappersData} from "api/api-hooks/dataset-mapper-hooks";
import {DatasetStringValueMapperDTO} from "components/dashboard/charts/interfaces";
import {DatasetInfo} from "api/model/dataset/DatasetInfo";
import {BoundType} from "utils/enums";
import {DATASET_ACCESS_TYPES, DATASET_DATA_TYPES} from "../../utils/utils";

interface IEditDatasetProps {
  selectedDatasetID: DatasetInfo[];
  setEditDatasetDialogVisibility: (value: boolean) => void;
  setSelectedDatasets: (arr: DatasetInfo[]) => void;
}

const emptyMapper: DatasetStringValueMapperDTO = {
  id: undefined,
  title: "None",
  global: false,
  mappings: {},
};

const boundToSelectItems = [
    { label: "Device ID", value: BoundType.MDM_DEVICE_ID},
    { label: "Device Identifier", value: BoundType.MDM_DEVICE_IDENTIFIER},
    { label: "Object ID", value: BoundType.MDM_OBJECT_ID},
    { label: "Object Identifier", value: BoundType.MDM_OBJECT_IDENTIFIER},
    { label: "Unbound/None", value: null }
];

const EditDatasetDialog = ({
  selectedDatasetID,
  setEditDatasetDialogVisibility,
  setSelectedDatasets,
}: IEditDatasetProps) => {
  const [datasetsArr, setDatasetsArr] = useState<DatasetInfo[]>(selectedDatasetID);
  const [mappers, setMappers] = useState<DatasetStringValueMapperDTO[]>([]);
  const { data: currentMappers } = useGetDatasetMappersData();

  useEffect(() => {
    if (currentMappers) {
      setMappers([emptyMapper, ...currentMappers]);
    }
  }, [currentMappers]);

  const titleBodyTemplate = (options: DatasetInfo) => {
    return (
      <InputText
        className="w-full"
        value={options.title || ""}
        name="title"
        onChange={e => inputChangeHandler(options, e)}
      />
    );
  };

  const dataTypeBodyTemplate = (options: DatasetInfo) => {
    return (
      <Dropdown
        className="w-full"
        options={DATASET_DATA_TYPES}
        value={options.dataType || ""}
        name="dataType"
        onChange={e => inputChangeHandler(options, e)}
      />
    );
  };

  const accessTypeBodyTemplate = (options: DatasetInfo) => {
    return (
      <Dropdown
        className="w-full"
        options={DATASET_ACCESS_TYPES}
        value={options.accessType || ""}
        name="accessType"
        onChange={e => inputChangeHandler(options, e)}
      />
    );
  };

  const mapperBodyTemplate = (options: DatasetInfo) => {
    return (
      <Dropdown
        className="w-full"
        value={options?.stringValueMapper?.toString() || ""}
        options={mappers}
        optionValue="id"
        optionLabel="title"
        name="stringValueMapper"
        onChange={e => inputChangeHandler(options, e)}
      />
    );
  };

  const unitBodyTemplate = (options: DatasetInfo) => {
    return (
      <DatasetUnitSelector
        className="w-8rem"
        value={options.unit || ""}
        name="unit"
        onChange={e => inputChangeHandler(options, e)}
      />
    );
  };

  const decimalPlacesBodyTemplate = (options: DatasetInfo) => {
    return (
      <InputNumber
        value={options.decimalPlaces ?? null}
        onValueChange={e => inputChangeHandler(options, e)}
        name="decimalPlaces"
        min={0}
        max={10}
        inputStyle={{ width: "100%" }}
      />
    );
  };  

  const deviceIdBodyTemplate = (options: DatasetInfo) => {
    return (
      <InputNumber
        value={options.deviceId || 0}
        name="deviceId"
        onValueChange={e => inputChangeHandler(options, e)}
        inputStyle={{ width: "5rem" }}
      />
    );
  };

  const boundToIdBodyTemplate = (options: DatasetInfo) => {
    return (
      <InputText
        className="w-10rem"
        value={options.boundToId || ""}
        name="boundToId"
        onChange={e => inputChangeHandler(options, e)}
      />
    );
  };

  const boundToTypeBodyTemplate = (options: DatasetInfo) => {
    return (
      <Dropdown
        className="w-14rem"
        value={options?.boundToType}
        options={boundToSelectItems}
        name="boundToType"
        onChange={e => inputChangeHandler(options, e)}
      />
    );
  };

  const inputChangeHandler = (
    options: DatasetInfo,
    e: InputNumberValueChangeParams | DropdownChangeParams | ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    console.log(value)
    setDatasetsArr(prev =>
      prev.map(dataset =>
        dataset.identifier === options.identifier ? { ...dataset, [name]: value } : dataset
      )
    );
  };

  const { mutateAsync } = useUpdateDatasetInfo(true);

  const saveChangesHandler = async () => {
    try {
      if (datasetsArr.length === 0) {
        easasAlert({
          summary: "No changes detected",
          detail: "Please make changes before saving.",
          severity: "warn",
        });
        return;
      }

      const results = await Promise.allSettled(
        datasetsArr.map(async dataset => {
          return mutateAsync(dataset);
        })
      );

      const successFullMutations = results.filter(result => result.status === "fulfilled");
      const failedMutations = results.filter(result => result.status === "rejected");

      if (successFullMutations.length === results.length) {
        easasAlert({
          summary: "Success",
          detail: "All changes were saved successfully.",
          severity: "success",
        });
      } else {
        easasAlert({
          summary: "Error",
          detail: `${failedMutations.length} datasets failed to update`,
          severity: "error",
        });
      }
    } catch (e) {
      console.log("Error updating datasets", e);
      easasAlert({
        summary: "Error",
        detail: "An error occurred while updating datasets",
        severity: "error",
      });
    }

    setEditDatasetDialogVisibility(false);
    setSelectedDatasets([]);
  };

  return (
    <div className="flex flex-column gap-3">
      <DataTable
        value={datasetsArr}
        className="p-datatable-gridlines mb-5"
        style={{ width: "100%" }}
      >
        <Column field="title" header="Title" className="w-full p-0" body={titleBodyTemplate} />
        <Column field="dataType" header="Type" className="w-1 p-0" body={dataTypeBodyTemplate} />
        <Column
          field="accessType"
          header="Access"
          className="w-1 p-0"
          body={accessTypeBodyTemplate}
        />
        <Column field="mapper" header="Mapper" className="w-1 p-0" body={mapperBodyTemplate} />
        <Column field="unit" header="Unit" className="p-0" body={unitBodyTemplate} />
        <Column
          field="decimalPlaces"
          header="Decimal Places"
          style={{ width: "4rem" }}
          className="p-0"
          body={decimalPlacesBodyTemplate}
        />
        <Column field="deviceId" header="Device ID" className="p-0" body={deviceIdBodyTemplate} />
        <Column
          field="boundToId"
          header="Bound To ID"
          className="p-0"
          body={boundToIdBodyTemplate}
        />
        <Column
          field="boundToType"
          header="Bound To Type"
          className="w-1 p-0"
          body={boundToTypeBodyTemplate}
        />
      </DataTable>
      <div className="buttons flex gap-2 mt-2">
        <Button
          label="Save"
          icon="pi pi-check"
          onClick={saveChangesHandler}
          className="p-button-success"
        />
        <Button
          label="Cancel"
          icon="pi pi-times"
          onClick={() => setEditDatasetDialogVisibility(false)}
          className="p-button-danger"
        />
      </div>
    </div>
  );
};

export default EditDatasetDialog;